import type { ReactNode } from 'react';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@bms2/shared';
import styles from './Money.module.css';

/**
 * The one render path for money on a screen (decision 0481). A negative amount
 * reads red; everything else reads as it always did.
 *
 * 🔨 The colour is never the only signal. The minus sign the formatter already
 * wrote stays in the text, so the number still reads as negative to a
 * colour-blind reader and on a black-and-white print.
 *
 * 🔨 A piece of text carrying no negative amount renders as bare text and adds
 * no element. Converting a site therefore changes no markup, no class and no
 * data attribute until the figure it draws actually goes below zero.
 */

/**
 * A negative currency amount inside a piece of text: a minus sign, an optional
 * currency prefix of up to three letters, a symbol, then digits — matching
 * `-$18,240.00`, `-€5.00` and `-CA$5.00`.
 *
 * 🔨 The symbol is required and is a currency symbol (Unicode `Sc`), and a fresh
 * matcher is built for each read. Drop the symbol and `2026-09-17` matches on its
 * month, so a date would draw half of itself in red; let any punctuation stand
 * for the symbol and an item named *Bulletin 100 -C, 220 V* drew its coil
 * voltage in red (the owner's report of 2026-10-06); share one matcher and its
 * `lastIndex` leaks between reads.
 */
const negativeAmounts = (): RegExp => /-[A-Za-z]{0,3}\p{Sc}\s?\d[\d,]*(?:\.\d+)?/gu;

/** Whether a piece of text carries a negative amount anywhere inside it. */
export function readsNegative(text: string): boolean {
  return negativeAmounts().test(text);
}

/**
 * Already-formatted text, with every negative amount inside it drawn red. The
 * text around the amounts is untouched, so a sentence such as
 * *"Difference -$5.00"* keeps its wording and reddens only the figure.
 */
export function MoneyText({ text }: { text: string }): ReactNode {
  const matcher = negativeAmounts();
  const parts: ReactNode[] = [];
  let cursor = 0;
  let found = matcher.exec(text);
  while (found !== null) {
    const amount = found[0];
    if (found.index > cursor) parts.push(text.slice(cursor, found.index));
    parts.push(
      <span key={`${String(found.index)}-${amount}`} className={styles.negative} data-negative="true">
        {amount}
      </span>,
    );
    cursor = found.index + amount.length;
    found = matcher.exec(text);
  }
  if (parts.length === 0) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/**
 * 🔒 A value a prop carries that may be money (decision 0717): a node the page
 * composed — the Money component inside a sentence — draws as it stands, and
 * text somebody already formatted draws through the text form, so a negative
 * amount inside it still reads red. The four props that carried money as text
 * take a node and draw it through this one function.
 */
export function drawnMoney(value: ReactNode): ReactNode {
  return typeof value === 'string' || typeof value === 'number' ? <MoneyText text={String(value)} /> : value;
}

/**
 * Integer cents as the person reads them, red when the figure is below zero.
 * 🔨 A figure whose amount has not arrived draws nothing rather than throwing
 * inside render: a totals box reads its tax from an answer that lands a moment
 * after the lines, and a page that crashed for that moment took the whole
 * record view with it (run 897, 2026-09-25).
 */
export function Money({ cents, currencyCode = DEFAULT_CURRENCY_CODE }: { cents: number; currencyCode?: string }): ReactNode {
  if (!Number.isInteger(cents)) return null;
  return <MoneyText text={formatCurrency({ amount: cents, currencyCode })} />;
}
